import React from 'react';
import { cn } from '../lib/cn';

/**
 * Avatar — Avatar con iniciales o imagen.
 *
 * El color de fondo se genera deterministicamente desde el nombre
 * para que siempre sea el mismo color para la misma persona.
 *
 * Uso:
 *   <Avatar name="Iban Lopetegi" />
 *   <Avatar name="Iban Lopetegi" size="sm" />
 *   <Avatar name="Iban Lopetegi" src="https://..." />
 */
const SIZES = {
    xs: 'w-6 h-6 text-[9px]',
    sm: 'w-7 h-7 text-[10px]',
    md: 'w-9 h-9 text-xs',
    lg: 'w-11 h-11 text-sm',
};

const PALETTE = [
    '#f59e0b', '#8b5cf6', '#10b981', '#ec4899', '#3b82f6',
    '#06b6d4', '#ef4444', '#84cc16', '#a855f7', '#0ea5e9',
    '#f97316', '#14b8a6',
];

function colorFromName(name) {
    if (!name) return PALETTE[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash << 5) - hash + name.charCodeAt(i);
        hash |= 0;
    }
    return PALETTE[Math.abs(hash) % PALETTE.length];
}

function initialsFromName(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const Avatar = ({ name, src, size = 'md', className, ...props }) => {
    const initials = initialsFromName(name);
    const bg = colorFromName(name);

    if (src) {
        return (
            <img
                src={src}
                alt={name}
                className={cn(
                    'rounded-full object-cover shrink-0',
                    SIZES[size],
                    className
                )}
                {...props}
            />
        );
    }

    return (
        <div
            className={cn(
                'rounded-full flex items-center justify-center font-semibold text-white shrink-0',
                SIZES[size],
                className
            )}
            style={{ background: bg }}
            title={name}
            {...props}
        >
            {initials}
        </div>
    );
};
